import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { fetchUserWishlistIds } from '../api/Wishlist';

interface WishlistContextType {
    wishlistIds: Set<number>;
    isInWishlist: (id: number) => boolean;
    toggleWishlistId: (id: number) => void;
    refreshWishlist: () => void;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

const CACHE_KEY = 'app_wishlist_ids';

function getCachedIds(): number[] {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) return JSON.parse(raw);
    } catch {}
    return [];
}

function setCachedIds(ids: number[]) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(ids));
    } catch {}
}

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [wishlistIds, setWishlistIds] = useState<Set<number>>(() => new Set(getCachedIds()));

    const fetchWishlist = useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setWishlistIds(new Set());
            setCachedIds([]);
            return;
        }
        try {
            const ids = await fetchUserWishlistIds();
            setWishlistIds(new Set(ids));
            setCachedIds(ids);
        } catch (err) {
            console.error('Failed to fetch wishlist:', err);
        }
    }, []);

    useEffect(() => {
        fetchWishlist();
    }, [fetchWishlist]);

    const isInWishlist = useCallback((id: number) => wishlistIds.has(id), [wishlistIds]);

    const toggleWishlistId = useCallback((id: number) => {
        setWishlistIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            setCachedIds(Array.from(next));
            return next;
        });
    }, []);

    return (
        <WishlistContext.Provider value={{ wishlistIds, isInWishlist, toggleWishlistId, refreshWishlist: fetchWishlist }}>
            {children}
        </WishlistContext.Provider>
    );
};

export function useWishlistContext() {
    const context = useContext(WishlistContext);
    if (!context) {
        throw new Error('useWishlistContext must be used within WishlistProvider');
    }
    return context;
}
